/**
 * The Path A screen-generation request, as a SERVICE (final review Important #3).
 *
 * HB-6 routed «create block with AI» to the same `AgentApiClient.updateBlock`
 * request the block-update dialog sends — but it built it inside
 * `BlockFormDialog`, with a dynamic `firebase/auth` import and an `alert()` for
 * the failure. Components are UI + layout; the business call lives here, and the
 * dialog renders the outcome inline.
 *
 * The wire is UNCHANGED: same endpoint, same keys, same values (developer rule
 * 25 — Path A is the only block-update path, and the server resolves every bit of
 * generation context from the strategy snapshot, so no source data travels from
 * the form).
 *
 * `AgentApiClient` is still imported DYNAMICALLY: it reads `import.meta.env`,
 * which no CommonJS test runtime can parse, so a static import here would break
 * every suite that merely imports this module.
 */
import type { StrategyBlock } from '@segmently/schema-models/strategy';

export interface RequestBlockScreensInput {
  projectId: string;
  strategyId: string;
  /** The block as it is STORED — the generation reads it from the server. */
  block: StrategyBlock & { id: string };
  /** The operator's generation prompt for this block. */
  prompt: string;
}

export interface RequestBlockScreensResult {
  success: boolean;
  /** The backend task the server started, when it started one. */
  taskId?: string;
  /** The server's own reason, or the transport failure, for an inline surface. */
  error?: string;
}

/**
 * Builds the `POST /api/agent/update-block` body. Pure, so the shape can be
 * asserted without a client that cannot be loaded under jest.
 */
export function buildBlockScreensRequest(
  input: RequestBlockScreensInput,
  userId: string,
): Record<string, unknown> {
  return {
    projectId: input.projectId,
    strategyId: input.strategyId,
    blockId: input.block.id,
    blockType: (input.block.blockType as string) || 'custom',
    prompt: input.prompt,
    userId,
    currentBlock: {
      ...(input.block as unknown as Record<string, unknown>),
      screens: [],
    },
  };
}

/** The message shown when the server refused without naming a reason. */
export const BLOCK_SCREENS_REQUEST_FALLBACK_ERROR =
  'The block was created, but the AI generation request failed. '
  + 'You can add screens manually or start the generation again.';

/**
 * Asks the server to generate this block's screens. Never throws: a refusal and a
 * transport failure both come back as `{ success: false, error }`, because the
 * caller has already created the block and must state what happened.
 */
export async function requestBlockScreens(
  input: RequestBlockScreensInput,
): Promise<RequestBlockScreensResult> {
  try {
    const [{ getAuth }, { AgentApiClient }] = await Promise.all([
      import('firebase/auth'),
      import('@modules/content-agent/services/AgentApiClient'),
    ]);
    const userId = getAuth().currentUser?.uid || 'unknown';
    const response = await new AgentApiClient().updateBlock(
      buildBlockScreensRequest(input, userId) as never,
    );

    if (response?.success === false) {
      return { success: false, error: response.error || BLOCK_SCREENS_REQUEST_FALLBACK_ERROR };
    }
    return { success: true, ...(response?.taskId ? { taskId: response.taskId } : {}) };
  } catch (error) {
    console.error('[requestBlockScreens] AI generation request failed:', error);
    return {
      success: false,
      error: (error as Error)?.message || BLOCK_SCREENS_REQUEST_FALLBACK_ERROR,
    };
  }
}
